import { useRef, useState } from 'react';
import { C, SERIF, MONO, mono } from '../theme';
import { Icon, Mark } from '../icons';
import { api, ApiError } from '../api';
import type { Me } from '../api';

type Step = 'identifier' | 'code';

/**
 * OTP login (SPEC §6). Two steps: request a code for an email/phone, then verify.
 * On success we hand the resolved `Me` up so the shell can route by role.
 * No password — the tenant surface must work OTP-only.
 */
/** Read a magic-link invite identity from `#/login?id=<contact>` (once). */
function invitedIdentity(): string {
  try {
    const h = typeof window !== 'undefined' ? window.location.hash : '';
    const q = h.includes('?') ? h.slice(h.indexOf('?') + 1) : '';
    return new URLSearchParams(q).get('id')?.trim() ?? '';
  } catch {
    return '';
  }
}

export function Login({ onAuthed }: { onAuthed: (me: Me) => void }) {
  const [invited] = useState(invitedIdentity);
  const [step, setStep] = useState<Step>('identifier');
  const [identifier, setIdentifier] = useState(invited);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  async function submitIdentifier(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.requestOtp(identifier.trim());
      // Local dev convenience: the Worker may echo the code when Resend is off.
      setDevCode(res?.devCode ?? null);
      setStep('code');
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (err) {
      setError(friendly(err, 'Could not send the code. Check the address and try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (code.trim().length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const me = await api.verifyOtp(identifier.trim(), code.trim());
      onAuthed(me);
    } catch (err) {
      setError(friendly(err, 'That code did not match. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          width: 380,
          maxWidth: '100%',
          background: C.card,
          border: `1px solid ${C.line}`,
          borderRadius: 18,
          padding: '30px 28px 26px',
          boxShadow: '0 2px 6px rgba(20,30,25,.06),0 24px 60px rgba(20,30,25,.14)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 20 }}>
          <Mark size={36} />
          <div>
            <div style={{ fontFamily: SERIF, fontSize: 23, fontWeight: 600, lineHeight: 1.05 }}>
              Premise
            </div>
            <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.04em', color: C.muted }}>
              your tenancy, on the record
            </div>
          </div>
        </div>

        {step === 'identifier' ? (
          <form onSubmit={submitIdentifier}>
            <h1 style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 600, margin: '0 0 4px' }}>
              {invited ? 'Welcome to Premise' : 'Sign in'}
            </h1>
            <p style={{ color: C.muted, fontSize: 13.3, margin: '0 0 18px' }}>
              {invited
                ? "You've been invited to your tenancy. Confirm the contact below and we'll send a one-time code, no password."
                : "We'll send a one-time code to your email. No password."}
            </p>
            <label style={label}>Email address</label>
            <div style={inputWrap}>
              <Icon n="mail" size={16} color={C.muted} />
              <input
                type="email"
                inputMode="email"
                autoFocus
                autoComplete="email"
                placeholder="you@example.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                style={input}
              />
            </div>
            {error && <div style={errBox}>{error}</div>}
            <button type="submit" disabled={busy || !identifier.trim()} style={primaryBtn(busy || !identifier.trim())}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <h1 style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 600, margin: '0 0 4px' }}>
              Enter the code
            </h1>
            <p style={{ color: C.muted, fontSize: 13.3, margin: '0 0 18px' }}>
              Sent to <b style={{ color: C.ink }}>{identifier}</b>.{' '}
              <span
                onClick={() => {
                  setStep('identifier');
                  setCode('');
                  setError(null);
                }}
                style={{ color: C.accent, fontWeight: 600, cursor: 'pointer' }}
              >
                Change
              </span>
            </p>
            {devCode && (
              <div style={devBox}>
                Dev code: <b style={mono({ letterSpacing: '.14em' })}>{devCode}</b>
              </div>
            )}
            <label style={label}>6-digit code</label>
            <div style={inputWrap}>
              <input
                ref={codeRef}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder="••••••"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ ...input, ...mono({ letterSpacing: '.32em', fontSize: 18 }) }}
              />
            </div>
            {error && <div style={errBox}>{error}</div>}
            <button type="submit" disabled={busy || code.length < 4} style={primaryBtn(busy || code.length < 4)}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
            <button
              type="button"
              onClick={submitIdentifier}
              disabled={busy}
              style={ghostBtn}
            >
              Resend code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function friendly(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // Prefer the server message when it's user-safe; otherwise the fallback.
    if (err.status === 429) return 'Too many attempts. Wait a moment and try again.';
    if (err.message && err.status < 500) return err.message;
  }
  // Network / API-down: keep it calm and non-technical.
  return fallback;
}

/* --- styles --- */
const label: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: C.ink2,
  display: 'block',
  marginBottom: 6,
};
const inputWrap: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: C.bg,
  border: `1px solid ${C.line}`,
  borderRadius: 10,
  padding: '11px 12px',
};
const input: React.CSSProperties = {
  flex: 1,
  border: 0,
  outline: 'none',
  background: 'transparent',
  fontSize: 14.5,
  color: C.ink,
  minWidth: 0,
};
const errBox: React.CSSProperties = {
  marginTop: 12,
  background: C.redSoft,
  color: C.red,
  fontSize: 12.8,
  fontWeight: 500,
  padding: '9px 11px',
  borderRadius: 9,
};
const devBox: React.CSSProperties = {
  marginBottom: 14,
  background: C.amberSoft,
  color: '#7a5518',
  fontSize: 12.8,
  padding: '9px 11px',
  borderRadius: 9,
};
function primaryBtn(disabled: boolean): React.CSSProperties {
  return {
    marginTop: 18,
    width: '100%',
    border: 0,
    borderRadius: 11,
    padding: 13,
    fontWeight: 650,
    fontSize: 14.5,
    color: '#fff',
    background: disabled ? '#c99a86' : C.accent,
    cursor: disabled ? 'default' : 'pointer',
  };
}
const ghostBtn: React.CSSProperties = {
  marginTop: 8,
  width: '100%',
  border: 0,
  borderRadius: 11,
  padding: 10,
  fontWeight: 600,
  fontSize: 13,
  color: C.accent,
  background: 'transparent',
  cursor: 'pointer',
};
